/**
 * Hook for tracking onboarding progress
 */
import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { settingsApi } from '../api/settings'
import { useAccounts } from './useAccounts'
import { useHoldings } from './useHoldings'
import { usePhilosophies, useTheses } from './useThesis'

const DISMISSED_KEY = 'portfolio-tracker-onboarding-dismissed'
const DISMISSED_EVENT = 'onboarding-dismissed-change'

export interface OnboardingStep {
  id: string
  title: string
  description: string
  isComplete: boolean
  href: string
}

export interface OnboardingProgress {
  steps: OnboardingStep[]
  percentComplete: number
  isEnrolled: boolean
  isDismissed: boolean
  isLoading: boolean
  dismiss: () => void
  reset: () => void
}

interface UseOnboardingOptions {
  /** Only the widget should auto-enroll, not other consumers like ProfileTab */
  autoEnroll?: boolean
}

export function useOnboardingProgress(options: UseOnboardingOptions = {}): OnboardingProgress {
  const { autoEnroll = false } = options
  const queryClient = useQueryClient()

  // localStorage state for dismissal - synced across hook instances via custom event
  const [isDismissed, setIsDismissed] = useState(() => {
    if (typeof window === 'undefined') return false
    return localStorage.getItem(DISMISSED_KEY) === 'true'
  })

  // Listen for changes from other hook instances
  useEffect(() => {
    const handleDismissedChange = () => {
      const dismissed = localStorage.getItem(DISMISSED_KEY) === 'true'
      setIsDismissed(dismissed)
    }
    window.addEventListener(DISMISSED_EVENT, handleDismissedChange)
    return () => window.removeEventListener(DISMISSED_EVENT, handleDismissedChange)
  }, [])

  // Backend enrollment status
  const { data: onboardingStatus, isLoading: statusLoading } = useQuery({
    queryKey: ['onboarding', 'status'],
    queryFn: settingsApi.getOnboardingStatus,
  })

  // Data queries to compute progress
  const { data: accounts, isLoading: accountsLoading } = useAccounts()
  const { data: holdings, isLoading: holdingsLoading } = useHoldings({})
  const { data: philosophies, isLoading: philosophiesLoading } = usePhilosophies()
  const { data: theses, isLoading: thesesLoading } = useTheses({ status: 'active' })

  const isLoading = statusLoading || accountsLoading || holdingsLoading || philosophiesLoading || thesesLoading

  // Enroll mutation - called when widget first displays
  const enrollMutation = useMutation({
    mutationFn: settingsApi.enrollOnboarding,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'status'] })
    },
  })

  // Reset mutation - clears backend enrollment so user can go through onboarding again
  const resetMutation = useMutation({
    mutationFn: settingsApi.resetOnboarding,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'status'] })
    },
  })

  // Compute steps from data
  const steps: OnboardingStep[] = useMemo(() => [
    {
      id: 'account',
      title: 'Create an Account',
      description: 'Add a brokerage account to start tracking',
      isComplete: (accounts?.length ?? 0) > 0,
      href: '/settings',
    },
    {
      id: 'holding',
      title: 'Add Your First Holding',
      description: 'Track a stock or asset in your portfolio',
      isComplete: (holdings?.length ?? 0) > 0,
      href: '/holdings',
    },
    {
      id: 'philosophy',
      title: 'Define Your Philosophy',
      description: 'Describe your investment worldview',
      isComplete: (philosophies?.length ?? 0) > 0,
      href: '/strategy/philosophy',
    },
    {
      id: 'thesis',
      title: 'Create a Thesis',
      description: 'Add a specific investment belief',
      isComplete: (theses?.length ?? 0) > 0,
      href: '/strategy/theses',
    },
  ], [accounts, holdings, philosophies, theses])

  const completedCount = steps.filter(s => s.isComplete).length
  const percentComplete = Math.round((completedCount / steps.length) * 100)

  const isEnrolled = !!onboardingStatus?.enrolled_at

  // Auto-enroll only when explicitly requested (from widget, not ProfileTab)
  useEffect(() => {
    if (autoEnroll && !statusLoading && !isEnrolled && !isDismissed) {
      enrollMutation.mutate()
    }
  }, [autoEnroll, statusLoading, isEnrolled, isDismissed])

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, 'true')
    setIsDismissed(true)
    window.dispatchEvent(new Event(DISMISSED_EVENT))
  }

  const reset = () => {
    // Clear localStorage dismissal
    localStorage.removeItem(DISMISSED_KEY)
    setIsDismissed(false)
    window.dispatchEvent(new Event(DISMISSED_EVENT))
    // Clear backend enrollment
    resetMutation.mutate()
  }

  return {
    steps,
    percentComplete,
    isEnrolled,
    isDismissed,
    isLoading,
    dismiss,
    reset,
  }
}
